import React, { Fragment, useMemo, useState } from 'react';
import { ITaxonomyContentProps } from '../../utils/types/taxonomy';
import Banner from '../category/Banner';
import ProductListHeading from '../common/ProductListHeading';
import ProductGrid from '../product/grids/ProductGrid';
import ProductCard from '../product/cards/ProductCard';
import SkeletonCategory from '../skeletons/SkeletonCategory';

import { isEmpty } from 'lodash';
import Filter from '../category/filter/Filter';
import ResultCount from '../category/filter/ResultCount';
import LoadMoreButton from '../category/LoadMoreButton';
import LoadingModal from '../common/LoadingModal';
import { useTaxonomyContext } from '../../contexts/Taxonomy';
import Description from '../category/Description';
import { useUpdateEffect } from 'usehooks-ts';
import { useRouter } from 'next/router';
import PerfectGiftSection from '../common/PerfectGiftSection';
import { decode } from 'html-entities';
import CyohProductCard from '../product/cards/CyohProductCard';
import { ITSProduct } from '../../utils/typesense/types/product';
import useFetchTsTaxonomyProducts from '../../utils/hooks/useFetchTsTaxonomyProducts';
import {
  ITSPaginationInfo,
  ITSTaxonomyProductQueryVars,
} from '../../utils/typesense/types/taxonomy';
import PageSeo from '../PageSeo';
import isBuildYourOwnHamper from '../../utils/product/isBuildYourOwnHamper';
import cyohProductData from '../../data/cyohProductData';
import valentinsDayData from '../../data/valentinesCardData';
import BannerPromoCatalogItem from '../category/BannerPromoCatalogItem';
import CatalogItem from '../product/cards/CatalogItem';
import easterCardData from '../../data/easterCardData';
import mothersDayCardData from '../../data/mothersDayCardData';
import carDealershipGiftsCardData from '../../data/carDealershipGiftsCardData';
import { cn } from '../../utils';
import ImageCatalogCard from '../product/cards/ImageCatalogCard';
import bulkOrderCardData from '../../data/bulkOrder';

const excludedForBannerPromo = [
  'sympathy',
  'sympathy gift baskets',
  'get well',
  'get well gift baskets',
  'wedding & engagement',
  "valentine's day",
  "valentine's day gift baskets",
  "father's day gift hampers",
  "mother's day gift baskets",
  "mother's day",
  "knox mother's day gifts",
];

export default function TaxonomyContent(props: ITaxonomyContentProps) {
  const taxonomyCtx = useTaxonomyContext();
  const [, setSlideOverFilterOpen] = taxonomyCtx.slideOverFilter;
  const [, setSlideOverSortByOpen] = taxonomyCtx.slideOverSort;

  const [, , selectedPriceFilter] = taxonomyCtx.priceFilter;
  const [, , selectedTypeFilter] = taxonomyCtx.typeFilter;
  const [, , selectedFavouriteFilter] = taxonomyCtx.favouriteFilter;
  const [, , selectedOcasionFilter] = taxonomyCtx.ocassionFilter;
  const [, , selectedSuitableForFilter] = taxonomyCtx.suitableForFilter;

  const router = useRouter();
  const showCustomTaxonomyBanner = router.asPath === '/occasion/mothers-day/';
  const isBabyRange = router.asPath.includes('baby');
  const [productsData, setProductsData] = useState<ITSProduct[]>(
    props.tsFetchedData.products ?? [],
  );

  const [tsPaginationInfo, setTsPaginationInfo] = useState<ITSPaginationInfo>(
    props.tsFetchedData.pageInfo ?? {},
  );

  const [tsQueryVars, setTsQueryVars] = useState(
    props.tsFetchedData.queryVars ?? {},
  );
  const cachedTsQueryVars = useMemo(() => tsQueryVars, [tsQueryVars]);

  const { loading, error, data } =
    useFetchTsTaxonomyProducts(cachedTsQueryVars);

  /**
   * After data fetch we append found products if needed
   */
  useUpdateEffect(() => {
    if (typeof data !== 'undefined' && false === loading) {
      setProductsData((prevProducts: ITSProduct[]) => {
        if (tsQueryVars.appendProducts == true) {
          // Append from previous
          return [...prevProducts, ...data?.products];
        }

        // Return new data
        return data?.products;
      });
      setTsQueryVars(data.queryVars);
      setTsPaginationInfo(data.pageInfo);
      setSlideOverFilterOpen(false);
      setSlideOverSortByOpen(false);
    }
  }, [data]);

  /**
   * Reset props on url slug change
   */
  useUpdateEffect(() => {
    setProductsData(props.tsFetchedData.products ?? []);
    setTsPaginationInfo(props.tsFetchedData.pageInfo ?? {});
    setTsQueryVars(props.tsFetchedData.queryVars ?? {});
  }, [router.asPath]);

  const onSortChange = (e: any) => {
    setTsQueryVars((prevProps: ITSTaxonomyProductQueryVars) => {
      const newProps: ITSTaxonomyProductQueryVars = {
        ...prevProps,
        appendProducts: false,
        sortBy: e.target.value,
      };
      return newProps;
    });
  };

  const applyFilter = () => {
    setTsQueryVars((prevProps: ITSTaxonomyProductQueryVars) => {
      const [, , selectedPriceFilter] = taxonomyCtx.priceFilter;
      const [, , selectedTypeFilter] = taxonomyCtx.typeFilter;
      const [, , selectedFavouriteFilter] = taxonomyCtx.favouriteFilter;
      const [, , selectedOcasionFilter] = taxonomyCtx.ocassionFilter;
      const [, , selectedSuitableForFilter] = taxonomyCtx.suitableForFilter;

      let nameTypeArr: string[] = [];

      if (!isEmpty(selectedTypeFilter)) {
        nameTypeArr.push(selectedTypeFilter.value);
      }

      if (!isEmpty(selectedFavouriteFilter)) {
        nameTypeArr.push(selectedFavouriteFilter.value);
      }

      if (!isEmpty(selectedOcasionFilter)) {
        nameTypeArr.push(selectedOcasionFilter.value);
      }

      if (!isEmpty(selectedSuitableForFilter)) {
        nameTypeArr.push(selectedSuitableForFilter.value);
      }

      let newProps: ITSTaxonomyProductQueryVars = {
        ...prevProps,
        page: 1,
        nameAndTypes: nameTypeArr,
        appendProducts: false,
        priceFilter: '',
      };

      if (!isEmpty(selectedPriceFilter)) {
        newProps = {
          ...newProps,
          priceFilter: selectedPriceFilter.value,
        };
      }

      return newProps;
      // return prevProps;
    });
  };

  /**
   * On product filter state change then we apply the filter
   */
  useUpdateEffect(applyFilter, [selectedPriceFilter]);
  useUpdateEffect(applyFilter, [selectedTypeFilter]);
  useUpdateEffect(applyFilter, [selectedFavouriteFilter]);
  useUpdateEffect(applyFilter, [selectedOcasionFilter]);
  useUpdateEffect(applyFilter, [selectedSuitableForFilter]);

  const loadMoreItems = () => {
    setTsQueryVars((prevProps: ITSTaxonomyProductQueryVars) => {
      if (tsPaginationInfo.nextPage) {
        const newProps: ITSTaxonomyProductQueryVars = {
          ...prevProps,
          page: tsPaginationInfo.nextPage,
          appendProducts: true,
        };

        return newProps;
      }

      return prevProps;
    });
  };

  return (
    <>
      <PageSeo seoFullHead={props.fullHead} />
      <main className="mb-10">
        <LoadingModal isOpen={loading} />
        <Banner {...props.hero} />
        <div className="container">
          <Filter
            applyFilter={applyFilter}
            pageNo={tsPaginationInfo.page}
            productCount={tsPaginationInfo.totalFound}
            onSortChange={onSortChange}
            taxonomyFilterOptions={props.tsFetchedData.taxonomyFilterOptions}
            priceFilter={props.tsFetchedData.priceFilter}
          />

          {productsData.length > 0 ? (
            <>
              {router.asPath === '/occasion/christmas-gift-hampers/' && (
                <ProductListHeading
                  heading=""
                  subHeading=""
                  className="text-center uppercase"
                />
              )}

              <ProductListHeading
                heading="Browse"
                subHeading={props.categoryName}
              />
              {props.topDescription && (
                <p className="text-center text-sm text-[#7A7A7A] my-8 font-light">
                  {decode(props.topDescription)}
                </p>
              )}
              {props.showPerfectGiftHelper && (
                <PerfectGiftSection
                  guideKey={props.guideKey}
                  heading="Let us help you find the perfect gift"
                />
              )}

              <div className="mx-5 xl:mx-0">
                <ProductGrid className={``}>
                  <>
                    {productsData.map((product: ITSProduct, index: number) => {
                      const isBYOH =
                        product.slug && isBuildYourOwnHamper(product.slug);

                      const orderSequence = index * 2;

                      const RealProductItem = () => {
                        return (
                          <div
                            style={{
                              order: orderSequence,
                            }}
                            className={cn(isBYOH ? ' byoh-grid-order' : '')}
                          >
                            {isBYOH ? (
                              <ImageCatalogCard product={cyohProductData} />
                            ) : (
                              <ProductCard
                                showRating
                                key={index}
                                product={product}
                              />
                            )}
                          </div>
                        );
                      };

                      if (
                        orderSequence === 6 &&
                        router.asPath === '/product-type/car-dealership-gifts/'
                      ) {
                        return (
                          <Fragment key={index}>
                            <RealProductItem />
                            <div
                              style={{
                                order: orderSequence,
                              }}
                              className="car-dealership-gifts-catalog-item"
                            >
                              <ImageCatalogCard
                                product={carDealershipGiftsCardData}
                              />
                            </div>
                          </Fragment>
                        );
                      }

                      if (
                        orderSequence === 0 &&
                        router.asPath === '/occasion/mothers-day--------------/'
                      ) {
                        return (
                          <Fragment key={index}>
                            <RealProductItem />
                            <div
                              style={{
                                order: orderSequence,
                              }}
                              className="mothers-day-item"
                            >
                              <CatalogItem product={mothersDayCardData} />
                            </div>
                          </Fragment>
                        );
                      }

                      if (
                        orderSequence === -1 &&
                        router.asPath !== '/occasion/easter-gift-hampers/'
                      ) {
                        return (
                          <Fragment key={index}>
                            <RealProductItem />
                            <div
                              style={{
                                order: orderSequence,
                              }}
                              className="second-catalog-item"
                            >
                              <CatalogItem product={easterCardData} />
                            </div>
                          </Fragment>
                        );
                      }
                      if (orderSequence === -1) {
                        return (
                          <Fragment key={index}>
                            <RealProductItem />
                            <div
                              style={{
                                order: orderSequence,
                              }}
                              className="valentines-day"
                            >
                              <CatalogItem product={valentinsDayData} />
                            </div>
                          </Fragment>
                        );
                      }
                      return (
                        <Fragment key={index}>
                          <RealProductItem />

                          {showCustomTaxonomyBanner && orderSequence == 5 && (
                            <div
                              style={{
                                order: orderSequence,
                              }}
                              className="banner-promo-catalog-item"
                            >
                              <BannerPromoCatalogItem />
                            </div>
                          )}

                          {!isBabyRange &&
                            orderSequence == 4 &&
                            router.asPath !=
                              '/product-type/car-dealership-gifts/' && (
                              <div
                                style={{
                                  order: 5,
                                }}
                                className="bulk-order-grid-item"
                              >
                                <ImageCatalogCard product={bulkOrderCardData} />
                              </div>
                            )}
                        </Fragment>
                      );
                    })}
                  </>
                </ProductGrid>
              </div>

              {tsPaginationInfo.nextPage ? (
                <>
                  {loading ? (
                    <div className="mx-5 xl:mx-0">
                      <SkeletonCategory />{' '}
                    </div>
                  ) : (
                    <LoadMoreButton loadMoreItems={loadMoreItems} />
                  )}
                </>
              ) : null}

              <div className="flex justify-center xl:justify-start">
                <ResultCount
                  pageNo={tsPaginationInfo.page}
                  productCount={tsPaginationInfo.totalFound}
                />
              </div>
            </>
          ) : (
            <p>No products found</p>
          )}

          <Description description={props.taxonomyDescription} />
        </div>
      </main>
    </>
  );
}
